import React, { useEffect, useState } from "react";
import { Tabs, message, Form, Input, Button } from "antd";
import { useParams } from "react-router-dom";
import { FaCamera } from "react-icons/fa";
import drEasyApi from "../../../Api/Customapi";
import { useGetUserData } from "../../../hooks/useGetUserData";
import { getImg } from "../../../Api/getImg";
import { getTokenBySlug } from "../../../hooks/useNotficationQuery";
import ChangePassword from "./ChangePassword";
import { useLanguage } from "../../../context/LanguageContext";
import { slug } from "../../../utils/store";

const { TabPane } = Tabs;

const Settings = () => {
  const { t } = useLanguage();
  const { data: user, isLoading, error, refetch } = useGetUserData(slug);
  const token = getTokenBySlug(slug);

  const [form] = Form.useForm();
  const [photoId, setPhotoId] = useState("");
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (user) {
      form.setFieldsValue({
        firstName: user.firstName || "",
        lastName: user.lastName || "",
        email: user.email || "",
        phoneNumber: user.phoneNumber || "",
      });
      setPhotoId(user.imageId || "");
    }
  }, [user, form]);

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const allowedTypes = ["image/jpeg", "image/jpg", "image/png"];
    if (!allowedTypes.includes(file.type)) {
      message.error(t("settings.editProfile.invalidImageType"));
      return;
    }

    try {
      setUploading(true);
      const formDataImg = new FormData();
      formDataImg.append("file", file);

      const { data } = await drEasyApi.post(
        "/api/Storage/UploadImage",
        formDataImg,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setPhotoId(data.id);
    } catch (err) {
      console.error(err);
      message.error(t("settings.editProfile.imageUploadFailed"));
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (values) => {
    if (!token) {
      message.error(t("settings.editProfile.tokenMissing"));
      return;
    }

    setSubmitting(true);

    const requestBody = {
      firstName: values.firstName,
      lastName: values.lastName,
      about: "TESTING-123",
      phoneNumber: values.phoneNumber,
      imageId: photoId,
      isGuest: false,
    };

    try {
      const res = await drEasyApi.post(
        `/api/${slug}/User/EditProfile`,
        requestBody,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (res.data?.isSuccessStatusCode || res.status === 200) {
        message.success(t("settings.editProfile.success"));
        refetch();
      } else {
        message.error(res.data?.message || t("settings.editProfile.error"));
      }
    } catch (err) {
      console.error(err);
      message.error(t("settings.editProfile.serverError"));
    } finally {
      setSubmitting(false);
    }
  };

  if (isLoading)
    return <div className="text-center mt-20">{t("settings.loading")}</div>;
  if (error)
    return (
      <div className="text-center mt-20 text-red-500">
        {t("settings.loadError")}
      </div>
    );

  return (
    <>
    
    <style>{`
        .custom-tabs .ant-tabs-tab .ant-tabs-tab-btn {
          color: #555;
        }

        .custom-tabs .ant-tabs-tab.ant-tabs-tab-active .ant-tabs-tab-btn {
          color: #C9A84C !important;
        }

        .custom-tabs .ant-tabs-ink-bar {
          background-color: #C9A84C !important;
        }

        .custom-tabs .ant-tabs-tab:hover .ant-tabs-tab-btn {
          color: #C9A84C   !important;
        }
      `}</style>

        <div className="max-w-5xl mx-auto mt-20 bg-white p-6 rounded-xl shadow-lg mb-80">
      <Tabs tabPosition="left" defaultActiveKey="1" className="min-h-[400px] custom-tabs">
        {/* Edit Profile Tab */}
        <TabPane tab={t("settings.tabs.editProfile")} key="1">
          <h2 className="text-2xl text-center font-bold mb-6 text-primary">
            {t("settings.editProfile.title")}
          </h2>

          {/* Avatar */}
          <div className="flex justify-center mb-6">
            <div className="relative">
              <img
                src={getImg(photoId) || "/anonymous.png"}
                alt="Profile"
                className="w-32 h-32 rounded-full object-cover border-2 border-primary"
              />
              <label className="absolute bottom-0 right-0 w-8 h-8 bg-white rounded-full flex items-center justify-center shadow cursor-pointer">
                <FaCamera className="text-primary" />
                <input
                  type="file"
                  accept="image/png, image/jpeg, image/jpg"
                  onChange={handleImageUpload}
                  className="hidden"
                />
              </label>
            </div>
          </div>

          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            size="large"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4">
              <Form.Item
                name="firstName"
                label={
                  <span className="text-sm font-medium">
                    {t("settings.editProfile.firstName")}
                  </span>
                }
                rules={[
                  {
                    required: true,
                    message: t("settings.editProfile.firstNameRequired") || "First name is required",
                  },
                ]}
              >
                <Input className="w-full border px-3 py-2 rounded" />
              </Form.Item>

              <Form.Item
                name="lastName"
                label={
                  <span className="text-sm font-medium">
                    {t("settings.editProfile.lastName")}
                  </span>
                }
                rules={[
                  {
                    required: true,
                    message: t("settings.editProfile.lastNameRequired") || "Last name is required",
                  },
                ]}
              >
                <Input className="w-full border px-3 py-2 rounded" />
              </Form.Item>

              <Form.Item
                name="email"
                label={
                  <span className="text-sm font-medium">
                    {t("settings.editProfile.email")}
                  </span>
                }
              >
                <Input
                  disabled
                  className="w-full border px-3 py-2 rounded bg-gray-100"
                />
              </Form.Item>

              <Form.Item
                name="phoneNumber"
                label={
                  <span className="text-sm font-medium">
                    {t("settings.editProfile.phoneNumber")}
                  </span>
                }
                rules={[
                  {
                    required: true,
                    message: t("settings.editProfile.phoneRequired") || "Phone number is required",
                  },
                  {
                    pattern: /^[\+]?[0-9][\d]{0,15}$/,
                    message: t("settings.editProfile.phoneInvalid") || "Invalid phone number",
                  },
                ]}
              >
                <Input className="w-full border px-3 py-2 rounded" />
              </Form.Item>
            </div>

            <Form.Item className="mt-2">
              <Button
                htmlType="submit"
                loading={submitting}
                type=""
                className="bg-primary hover:bg-primary_hover  text-white px-6 py-2 rounded disabled:opacity-50 border-none"
              >
                {submitting
                  ? t("settings.editProfile.saving")
                  : t("settings.editProfile.saveButton")}
              </Button>
            </Form.Item>
          </Form>
        </TabPane>

        {/* Change Password Tab */}
        <TabPane tab={t("settings.tabs.changePassword")} key="2">
          <ChangePassword slug={slug} token={token} />
        </TabPane>
      </Tabs>

    </div>
    </>
  );
  
};

export default Settings;